import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from "react";
import Drawer from "react-modern-drawer";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  ButtonVariant,
  ButtonSize,
  Show,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { Loader } from "shared/components/Loader";
import { ApplicationFilters } from "../../index";
import { FilterSection } from "./FilterSection";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { ApplicationStatus, ScreeningStatus } from "shared/types";
import { getColumnFilterConfig } from "../../utils/columnFilters";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { NO_UNIT_TYPE } from "../../constants";
import {
  applicationFilterOptionsQueryKey,
  useApplicationFilterOptions
} from "../../hooks/useApplicationFilterOptions";
import {
  DATE_FILTER_KEYS,
  DATE_FILTER_LABELS,
  DateFilterKey,
  DateRange,
  DateRanges,
  isDateRangeInverted
} from "../../utils/dateRanges";
import { DateRangeFields } from "../ColumnFilters/DateRangeFields";

interface Props {
  filters?: ApplicationFilters;
  setFilters: Dispatch<SetStateAction<ApplicationFilters | undefined>>;
  showFilters: boolean;
  setShowFilters: Dispatch<SetStateAction<boolean>>;
  unitId?: number;
  propertyId?: number;
  /** A stage tab pins the stage, so the drawer does not offer a stage filter there. */
  isStageTabSelected?: boolean;
  /** A status tab pins the status the same way. */
  isStatusTabSelected?: boolean;
}

// Partner-wide units carry their property's name, since unit titles repeat across properties.
const unitLabel = (unit: { title: string; property_name?: string }) =>
  unit.property_name ? `${unit.title} · ${unit.property_name}` : unit.title;

// Drops empty ranges so an untouched date section does not count as a filter.
const compactDateRanges = (ranges: DateRanges): DateRanges | undefined => {
  const entries = Object.entries(ranges).filter(
    ([, range]) => range?.from || range?.to
  );
  return entries.length ? Object.fromEntries(entries) : undefined;
};

export const FiltersDrawer: FC<Props> = ({
  unitId,
  propertyId,
  filters,
  setFilters,
  setShowFilters,
  showFilters,
  isStageTabSelected = false,
  isStatusTabSelected = false
}) => {
  const { Unit, Property } = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();
  const { data: filterOptions, isLoading } = useApplicationFilterOptions({
    unitId,
    propertyId
  });

  useEffect(() => {
    if (showFilters) {
      queryClient.invalidateQueries({
        queryKey: applicationFilterOptionsQueryKey({ unitId, propertyId })
      });
      setSelectedStageIds(filters?.stageIds || []);
      setSelectedDateRanges(filters?.dateRanges || {});
      setSelectedStatuses(filters?.status || []);
      setSelectedStates(filters?.states || []);
      setSelectedProperties(filters?.properties || []);
      setSelectedCities(filters?.cities || []);
      setSelectedUnits(filters?.units || []);
      setSelectedUnitTypes(filters?.unitTypes || []);
      setSelectedScreeningStatuses(filters?.screeningStatuses || []);
      setSelectedUnitStatus(filters?.unitStatus);
      setSelectedPropertyGroups(filters?.propertyGroups || []);
    }
  }, [showFilters]);

  const [selectedProperties, setSelectedProperties] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedUnits, setSelectedUnits] = useState<
    {
      id: string;
      name: string;
    }[]
  >([]);
  const [selectedUnitTypes, setSelectedUnitTypes] = useState<string[]>([]);
  const [selectedScreeningStatuses, setSelectedScreeningStatuses] = useState<
    ScreeningStatus[]
  >([]);
  const [selectedUnitStatus, setSelectedUnitStatus] = useState<
    "listed" | "unlisted" | undefined
  >();
  const [selectedPropertyGroups, setSelectedPropertyGroups] = useState<
    { id: string; name: string }[]
  >([]);
  const [selectedStageIds, setSelectedStageIds] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<ApplicationStatus[]>(
    []
  );
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const statusOptions = useMemo(
    () =>
      getColumnFilterConfig("status", "Status", {
        filterOptions,
        isStageTabSelected,
        isStatusTabSelected,
        labels: { property: Property, unit: Unit }
      }),
    [filterOptions, isStageTabSelected, isStatusTabSelected, Property, Unit]
  );
  const [selectedDateRanges, setSelectedDateRanges] = useState<DateRanges>({});
  const hasInvertedRange = Object.values(selectedDateRanges).some(
    (range) => range && isDateRangeInverted(range)
  );

  const setDateRange = (key: DateFilterKey, range: DateRange) =>
    setSelectedDateRanges((prev) => ({ ...prev, [key]: range }));

  const applyFilters = () => {
    // Spread prev to keep what the drawer does not edit (e.g. a stage filter hidden on a stage tab).
    setFilters((prev) => ({
      ...prev,
      ...(isStageTabSelected
        ? {}
        : {
            stageIds: selectedStageIds.length ? selectedStageIds : undefined
          }),
      ...(isStatusTabSelected
        ? {}
        : { status: selectedStatuses.length ? selectedStatuses : undefined }),
      states: selectedStates.length ? selectedStates : undefined,
      dateRanges: compactDateRanges(selectedDateRanges),
      properties: selectedProperties,
      cities: selectedCities,
      units: selectedUnits,
      unitTypes: selectedUnitTypes,
      screeningStatuses: selectedScreeningStatuses,
      unitStatus: selectedUnitStatus,
      propertyGroups: selectedPropertyGroups.length
        ? selectedPropertyGroups
        : undefined
    }));
    setShowFilters(false);
  };

  return (
    <Drawer
      className="rounded-l-lg"
      direction="right"
      open={showFilters}
      onClose={() => setShowFilters(false)}
      size={360}
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-40">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-content-1000">Filters</h1>
          <Show if={isLoading}>
            <Loader size={25} />
          </Show>
        </div>
        <IconButton
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.SM}
          onClick={() => setShowFilters(false)}
          HugeIcon={Cancel01Icon}
        />
      </div>

      <div className="px-6">
        <div className="py-4 border-b border-alpha-40">
          <Button
            variant={ButtonVariant.TEXT}
            className="ml-2 text-base font-bold"
            onClick={() => {
              setSelectedProperties([]);
              setSelectedCities([]);
              setSelectedUnits([]);
              setSelectedUnitTypes([]);
              setSelectedScreeningStatuses([]);
              setSelectedUnitStatus(undefined);
              setSelectedPropertyGroups([]);
              setSelectedStageIds([]);
              setSelectedDateRanges({});
              setSelectedStatuses([]);
              setSelectedStates([]);
            }}
          >
            Unselect all
          </Button>
        </div>

        <Show
          if={!Object.values(filterOptions ?? {}).some((opt) => opt?.length)}
        >
          <p className="my-1 text-sm text-content-400">
            {isLoading ? "Loading..." : "No options available"}
          </p>
        </Show>

        <div className="divide-y divide-alpha-40 overflow-y-auto h-[calc(100vh-211px)] hide-scroll">
          <Show if={Boolean(filterOptions?.property_groups)}>
            <FilterSection
              title={`${Property} group`}
              options={filterOptions?.property_groups ?? []}
              optionLimit={5}
              selectedOptions={selectedPropertyGroups.map((g) => String(g.id))}
              onChange={(groupId) => {
                const id = String(groupId);
                setSelectedPropertyGroups((prev) =>
                  prev.some((g) => g.id === id)
                    ? prev.filter((g) => g.id !== id)
                    : [
                        ...prev,
                        {
                          id,
                          name:
                            filterOptions?.property_groups?.find(
                              (g) => String(g.id) === id
                            )?.name ?? ""
                        }
                      ]
                );
              }}
            />
          </Show>
          <Show if={Boolean(filterOptions?.properties)}>
            <FilterSection
              title={Property}
              options={filterOptions?.properties ?? []}
              optionLimit={5}
              selectedOptions={selectedProperties.map((property) =>
                String(property.id)
              )}
              onChange={(propertyId) => {
                const id = String(propertyId);
                setSelectedProperties((prev) =>
                  prev.some((p) => p.id === id)
                    ? prev.filter((p) => p.id !== id)
                    : [
                        ...prev,
                        {
                          id,
                          name:
                            filterOptions?.properties?.find(
                              (p) => String(p.id) === id
                            )?.name ?? ""
                        }
                      ]
                );
              }}
            />
          </Show>
          <Show if={Boolean(filterOptions?.cities)}>
            <FilterSection
              title="City"
              options={
                filterOptions?.cities?.map(({ city, count }) => ({
                  name: city,
                  count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedCities}
              onChange={(city) =>
                setSelectedCities((prev) =>
                  prev.includes(city)
                    ? prev.filter((c) => c !== city)
                    : [...prev, city]
                )
              }
            />
          </Show>
          <Show if={Boolean(filterOptions?.units)}>
            <FilterSection
              title={Unit}
              options={
                filterOptions?.units?.map((unit) => ({
                  id: String(unit.id),
                  name: unitLabel(unit),
                  count: unit.count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedUnits.map((unit) => String(unit.id))}
              onChange={(unitId) => {
                const id = String(unitId);
                const unit = filterOptions?.units?.find(
                  (u) => String(u.id) === id
                );
                setSelectedUnits((prev) =>
                  prev.some((u) => u.id === id)
                    ? prev.filter((u) => u.id !== id)
                    : [...prev, { id, name: unit ? unitLabel(unit) : "" }]
                );
              }}
            />
          </Show>
          <Show if={statusOptions?.kind === "values"}>
            <FilterSection
              title="Application status"
              options={
                statusOptions?.kind === "values"
                  ? statusOptions.options.map(({ value, label, count }) => ({
                      id: value,
                      name: label,
                      count: count ?? 0
                    }))
                  : []
              }
              optionLimit={10}
              selectedOptions={selectedStatuses}
              onChange={(value) => {
                const applicationStatus = value as ApplicationStatus;
                setSelectedStatuses((prev) =>
                  prev.includes(applicationStatus)
                    ? prev.filter((s) => s !== applicationStatus)
                    : [...prev, applicationStatus]
                );
              }}
            />
          </Show>
          <Show if={Boolean(filterOptions?.states?.length)}>
            <FilterSection
              title="State"
              options={
                filterOptions?.states?.map(({ state, count }) => ({
                  name: state,
                  count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedStates}
              onChange={(state) =>
                setSelectedStates((prev) =>
                  prev.includes(state)
                    ? prev.filter((s) => s !== state)
                    : [...prev, state]
                )
              }
            />
          </Show>
          <Show if={Boolean(filterOptions?.unit_types)}>
            <FilterSection
              title="Unit type"
              options={
                filterOptions?.unit_types?.map(({ unit_type, count }) => ({
                  name: unit_type === "" ? NO_UNIT_TYPE : unit_type,
                  count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedUnitTypes}
              onChange={(unitType) =>
                setSelectedUnitTypes((prev) =>
                  prev.includes(unitType)
                    ? prev.filter((type) => type !== unitType)
                    : [...prev, unitType]
                )
              }
            />
          </Show>
          <Show if={Boolean(filterOptions?.screening_statuses)}>
            <FilterSection
              title="Screening status"
              options={
                filterOptions?.screening_statuses?.map(({ status, count }) => ({
                  id: status,
                  name: status,
                  count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedScreeningStatuses}
              onChange={(status) => {
                setSelectedScreeningStatuses((prev) => {
                  const statusKey = status as ScreeningStatus;
                  return prev.includes(statusKey)
                    ? prev.filter((s) => s !== statusKey)
                    : [...prev, statusKey];
                });
              }}
            />
          </Show>
          <Show if={Boolean(filterOptions?.unit_statuses)}>
            <FilterSection
              title="Unit status"
              options={
                filterOptions?.unit_statuses?.map(({ status, count }) => {
                  const isAllowed = status === "applications_allowed";
                  return {
                    id: isAllowed ? "listed" : "unlisted",
                    name: isAllowed
                      ? "Applications allowed"
                      : "Applications not allowed",
                    count
                  };
                }) ?? []
              }
              optionLimit={2}
              selectedOptions={selectedUnitStatus ? [selectedUnitStatus] : []}
              onChange={(status) => {
                setSelectedUnitStatus((prev) => {
                  const statusKey = status as "listed" | "unlisted";
                  return prev === statusKey ? undefined : statusKey;
                });
              }}
            />
          </Show>
          <Show
            if={!isStageTabSelected && Boolean(filterOptions?.stages?.length)}
          >
            <FilterSection
              title="Stage"
              options={
                filterOptions?.stages?.map(({ id, name, count }) => ({
                  id: String(id),
                  name,
                  count
                })) ?? []
              }
              optionLimit={5}
              selectedOptions={selectedStageIds.map(String)}
              onChange={(stageId) => {
                const id = Number(stageId);
                setSelectedStageIds((prev) =>
                  prev.includes(id)
                    ? prev.filter((s) => s !== id)
                    : [...prev, id]
                );
              }}
            />
          </Show>
          {DATE_FILTER_KEYS.map((key) => (
            <div key={key} className="space-y-3 py-4">
              <p className="text-sm font-bold text-content-1000">
                {DATE_FILTER_LABELS[key]} date
              </p>
              <DateRangeFields
                idPrefix="drawer-filter"
                dateKey={key}
                range={selectedDateRanges[key] ?? {}}
                onChange={(range) => setDateRange(key, range)}
              />
            </div>
          ))}
        </div>

        <div className="absolute bottom-0 py-4 flex gap-2 border-t border-alpha-40">
          <Button
            className="w-[152px]"
            size={ButtonSize.BASE}
            onClick={() => setShowFilters(false)}
          >
            Cancel
          </Button>
          <Button
            className="w-[152px]"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.BASE}
            disabled={hasInvertedRange}
            onClick={applyFilters}
          >
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
